import React, { useEffect, useState } from 'react'
import './Navigation.css'
import { ProfilType } from './Profil.type'

interface Props {
  components: React.ReactNode[]
  handleFormSubmit: () => void
  setMissingValue: React.Dispatch<React.SetStateAction<boolean>>
  missingValue: boolean
  validation: [
    formDataValidPart1: {
      date: boolean
      heureVisite: boolean
    },
    formDataValidPart2: {
      proprietaire: boolean
      adresseBien: boolean
      ville: boolean
      codePostal: boolean
      typeBien: boolean
    },
    formDataValidPart3: {
      selectedProfile: boolean
    },
  ]
  authProps: {
    token: string
    isLoggedIn: boolean
    setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>
  }

  formData: {
    date: Date
    heureVisite: string
    noDateYet: boolean
    proprietaire: string
    adresseBien: string
    ville: string
    codePostal: string
    typeBien: string
    prix: { email: string; prix: number }[]
    informationsComplementaires: string
    selectedProfiles: ProfilType[]
  }
  setFormData: React.Dispatch<
    React.SetStateAction<{
      date: Date
      heureVisite: string
      noDateYet: boolean
      proprietaire: string
      adresseBien: string
      ville: string
      prix: { email: string; prix: number }[]
      informationsComplementaires: string
      codePostal: string
      typeBien: string
      selectedProfiles: ProfilType[]
    }>
  >
}

const Navigation: React.FC<Props> = ({
  authProps,
  formData,
  components,
  setFormData,
  handleFormSubmit,
  validation,
  setMissingValue,
  missingValue,
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [basketExist, setBasketExist] = useState(false)
  const totalSteps = components.length

  // Tableau d'étapes, chaque élément est un objet avec des propriétés 'active' et 'done'
  const [steps, setSteps] = useState<Array<{ active: boolean; done: boolean }>>(
    Array(totalSteps).fill({ active: false, done: false }),
  )
  useEffect(() => {
    // Logique pour mettre à jour formData si basketToFinish existe
    const dataBasket = localStorage.getItem('basketToFinish')
    const basketToFinish = dataBasket ? JSON.parse(dataBasket) : ''

    if (basketToFinish) {
      setBasketExist(true)
      setFormData(basketToFinish)

      setCurrentStep(3)
    }
  }, [])

  const stepNames = [
    'Planifier votre visite',
    'Ajouter des informations',
    'Choisir un visiteur',
    'Envoyer la demande',
  ]

  const markStepAsDone = (stepIndex: number) => {
    const updatedSteps = steps.map((step, index) => ({
      active: index === stepIndex,
      done: index < stepIndex,
    }))
    setSteps(updatedSteps)
  }

  useEffect(() => {
    markStepAsDone(currentStep)
  }, [currentStep])

  const nextStep = () => {
    // Mode "Je n'ai pas encore de date": depuis l'étape 1, on envoie directement vers la liste visiteurs.
    if (currentStep === 0 && formData.noDateYet) {
      setMissingValue(false)
      setCurrentStep(2)
      return
    }

    const valeurs = Object.values(validation[currentStep])
    const value = valeurs.every((valeur) => valeur)
    if (!value) {
      setMissingValue(true)
    } else {
      setMissingValue(false)
      if (currentStep < totalSteps - 1) {
        setCurrentStep(currentStep + 1)
      }
    }
  }

  const prevStep = () => {
    if (basketExist) localStorage.removeItem('basketToFinish')

    setMissingValue(false)
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }
  const isLastStep = currentStep === totalSteps - 1

  // Fonction pour calculer les classes CSS en fonction de l'état de l'étape
  const getStepState = (stepIndex: number) => {
    const step = steps[stepIndex]
    if (step.active) return 'active'
    if (step.done) return 'done'
    return 'todo'
  }

  return (
    <div>
      <ul className='flex flex-wrap items-stretch gap-2 sm:gap-3'>
        {components.map((_, index) => {
          const state = getStepState(index)

          let chipClasses = 'border-black/10 bg-white/60 text-gray-700'
          if (state === 'active') {
            chipClasses = 'border-[#f75000]/25 bg-white text-gray-900 shadow-sm'
          } else if (state === 'done') {
            chipClasses = 'border-emerald-200 bg-emerald-50 text-gray-900'
          }

          let dotClasses = 'bg-gray-500 text-white'
          if (state === 'active') {
            dotClasses = 'bg-gradient-to-r from-[#f40000] to-[#f75000] text-white'
          } else if (state === 'done') {
            dotClasses = 'bg-emerald-500 text-white'
          }

          return (
            <li
              key={index}
              className={`flex items-center gap-3 rounded-2xl border px-3 py-2 text-sm font-semibold ${chipClasses}`}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <span
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-extrabold ${dotClasses}`}
              >
                {index + 1}
              </span>
              <span className='whitespace-nowrap'>{stepNames[index]}</span>
            </li>
          )
        })}
      </ul>

      <div className='mt-6 rounded-2xl border border-black/10 bg-white/70 p-5 sm:p-7 shadow-sm'>
        {React.cloneElement(components[currentStep] as React.ReactElement)}
      </div>

      {missingValue && (
        <div className='mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800'>
          Merci de compléter l&apos;ensemble des champs.
        </div>
      )}

      <div className='mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='w-full sm:w-auto'>
          {currentStep > 0 && (
            <button
              className='w-full sm:w-auto inline-flex items-center justify-center rounded-xl border border-black/10 bg-white/70 px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f75000]/30'
              onClick={prevStep}
            >
              {basketExist ? 'Annuler' : 'Précédent'}
            </button>
          )}
        </div>

        <div className='w-full sm:w-auto'>
          {authProps.token ? (
            <button
              className='w-full sm:w-auto bg-orange inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-extrabold'
              onClick={isLastStep ? handleFormSubmit : nextStep}
            >
              {isLastStep ? 'Terminer' : 'Suivant'}
            </button>
          ) : (
            <>
              {isLastStep ? (
                <a className='block w-full sm:w-auto' href='/inscription'>
                  <button
                    onClick={() => localStorage.setItem('basketToFinish', JSON.stringify(formData))}
                    className='w-full sm:w-auto bg-orange inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-extrabold'
                  >
                    Connexion pour valider
                  </button>
                </a>
              ) : (
                <button
                  className='w-full sm:w-auto bg-orange inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-extrabold'
                  onClick={nextStep}
                >
                  Suivant
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Navigation
